import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseTestimonialCarouselOptions {
  total: number;
  initialIndex?: number;
  autoPlayInterval?: number;
}

export interface UseTestimonialCarouselResult {
  currentIndex: number;
  dragOffset: number;
  isDragging: boolean;
  isAtStart: boolean;
  isAtEnd: boolean;
  carouselRef: React.MutableRefObject<HTMLDivElement | null>;
  goTo: (index: number) => void;
  goNext: () => void;
  goPrev: () => void;
  pauseHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
  pointerHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: () => void;
  };
}

export const useTestimonialCarousel = ({
  total,
  initialIndex = 0,
  autoPlayInterval = 5000,
}: UseTestimonialCarouselOptions): UseTestimonialCarouselResult => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPaused, setIsPaused] = useState(false);
  const isKeyboardMode = useRef(false);

  useEffect(() => {
    const onKeyDown = () => {
      isKeyboardMode.current = true;
    };
    const onPointerDown = () => {
      isKeyboardMode.current = false;
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (total <= 0) return;
      setCurrentIndex(Math.min(Math.max(index, 0), total - 1));
    },
    [total],
  );

  const goNext = useCallback(() => {
    if (total <= 0) return;
    setCurrentIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);

  const goPrev = useCallback(() => {
    if (total <= 0) return;
    setCurrentIndex((i) => Math.max(i - 1, 0));
  }, [total]);

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex >= total - 1;

  useEffect(() => {
    if (isPaused || !autoPlayInterval || total <= 1) return undefined;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i >= total - 1 ? 0 : i + 1));
    }, autoPlayInterval);
    return () => clearInterval(id);
  }, [isPaused, autoPlayInterval, total]);

  const carouselRef = useRef<HTMLDivElement | null>(null);
  const pointerStartX = useRef<number | null>(null);
  const activePointerId = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const endDrag = useCallback(
    (commitDelta: number | null) => {
      const width = carouselRef.current?.clientWidth ?? 0;
      const threshold = Math.max(40, width * 0.15);
      if (commitDelta !== null && Math.abs(commitDelta) > threshold) {
        if (commitDelta < 0) goNext();
        else goPrev();
      }
      pointerStartX.current = null;
      activePointerId.current = null;
      setIsDragging(false);
      setDragOffset(0);
    },
    [goNext, goPrev],
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    pointerStartX.current = e.clientX;
    activePointerId.current = e.pointerId;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (pointerStartX.current === null || activePointerId.current !== e.pointerId) return;
    setDragOffset(e.clientX - pointerStartX.current);
  }, []);
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (pointerStartX.current === null) return;
      endDrag(e.clientX - pointerStartX.current);
    },
    [endDrag],
  );
  const onPointerCancel = useCallback(() => {
    endDrag(null);
  }, [endDrag]);

  const pauseHandlers = {
    onMouseEnter: () => setIsPaused(true),
    onMouseLeave: () => setIsPaused(false),
    onFocus: () => {
      if (isKeyboardMode.current) setIsPaused(true);
    },
    onBlur: () => setIsPaused(false),
  };

  return {
    currentIndex,
    dragOffset,
    isDragging,
    isAtStart,
    isAtEnd,
    carouselRef,
    goTo,
    goNext,
    goPrev,
    pauseHandlers,
    pointerHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
};
